import React, { useState, useEffect, useMemo } from 'react';
import axios from './lib/axiosClient';
import { useNavigate } from 'react-router-dom';
import useAutoRefresh from './hooks/useAutoRefresh';

function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerMembership, setCustomerMembership] = useState(null);
  const [customerGiftCards, setCustomerGiftCards] = useState([]);
  
  const [showSMS, setShowSMS] = useState(false);
  const [smsCustomer, setSmsCustomer] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  
  const [showQuickBook, setShowQuickBook] = useState(false);
  const [quickBookCustomer, setQuickBookCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [quickBookData, setQuickBookData] = useState({
    service_id: '', staff_id: '',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '10:00', notes: ''
  });

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    address: '', city: '', state: '', zip: '', birthday: '', notes: ''
  });

  useEffect(() => { loadCustomers(); loadBookingData(); }, []);

  const loadCustomers = async (silent) => {
    try {
      if (!silent) { setLoading(true); setError(null); }
      const response = await axios.get('/api/customers');
      if (response.data.success) setCustomers(response.data.customers || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading customers:', err);
      if (!silent) { setError('Failed to load customers'); }
      setLoading(false);
    }
  };

  const loadBookingData = async () => {
    try {
      const [servicesRes, staffRes] = await Promise.all([
        axios.get('/api/services?active_only=true'), axios.get('/api/staff')
      ]);
      if (servicesRes.data.success) setServices(servicesRes.data.services || []);
      if (staffRes.data.success) setStaff(staffRes.data.staff || []);
    } catch (err) { console.error('Error loading booking data:', err); }
  };

  useAutoRefresh(function() { loadCustomers(true); }, 20000);

  const daysSince = (date) => {
    if (!date) return null;
    return Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
  };

  const formatLastVisit = (date) => {
    if (!date) return 'Never';
    const days = daysSince(date);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return days + 'd ago';
    if (days < 30) return Math.floor(days / 7) + 'w ago';
    if (days < 365) return Math.floor(days / 30) + 'mo ago';
    return Math.floor(days / 365) + 'y ago';
  };

  const getCustomerBadge = (c) => {
    const spent = parseFloat(c.total_spent || 0);
    const days = daysSince(c.last_visit);
    const created = daysSince(c.created_at);
    if (spent >= 500) return { label: 'VIP', color: '#f59e0b', bg: '#f59e0b18' };
    if (created !== null && created <= 30) return { label: 'New', color: '#22c55e', bg: '#22c55e18' };
    if (days !== null && days > 90) return { label: 'Inactive', color: '#ef4444', bg: '#ef444418' };
    return null;
  };

  const cleanPhone = (phone) => (phone || '').replace(/[^0-9+]/g, '');

  const processedCustomers = useMemo(() => {
    let filtered = customers.filter(c =>
      c.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterBy === 'vip') filtered = filtered.filter(c => parseFloat(c.total_spent || 0) >= 500);
    else if (filterBy === 'new') filtered = filtered.filter(c => daysSince(c.created_at) <= 30);
    else if (filterBy === 'inactive') filtered = filtered.filter(c => daysSince(c.last_visit) > 90);

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'spent': return parseFloat(b.total_spent || 0) - parseFloat(a.total_spent || 0);
        case 'visits': return (b.total_visits || 0) - (a.total_visits || 0);
        case 'recent': return new Date(b.last_visit || 0) - new Date(a.last_visit || 0);
        default: return (a.first_name || '').localeCompare(b.first_name || '');
      }
    });
    return filtered;
  }, [customers, searchTerm, sortBy, filterBy]);

  const stats = useMemo(() => ({
    total: customers.length,
    vip: customers.filter(c => parseFloat(c.total_spent || 0) >= 500).length,
    newThisMonth: customers.filter(c => daysSince(c.created_at) <= 30).length,
    inactive: customers.filter(c => daysSince(c.last_visit) > 90).length,
  }), [customers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        const response = await axios.put('/api/customers/' + editingCustomer.id, formData);
        if (response.data.success) { closeModal(); loadCustomers(); }
      } else {
        const response = await axios.post('/api/customers', formData);
        if (response.data.success) { closeModal(); loadCustomers(); }
      }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      first_name: customer.first_name || '', last_name: customer.last_name || '',
      phone: customer.phone || '', email: customer.email || '',
      address: customer.address || '', city: customer.city || '',
      state: customer.state || '', zip: customer.zip || '',
      birthday: customer.birthday || '', notes: customer.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Delete this customer? This cannot be undone.')) return;
    try {
      const response = await axios.delete('/api/customers/' + customerId);
      if (response.data.success) { loadCustomers(); setSelectedCustomer(null); }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
  };

  const handleQuickBook = (customer) => {
    setQuickBookCustomer(customer);
    setQuickBookData({ service_id: '', staff_id: '', booking_date: new Date().toISOString().split('T')[0], start_time: '10:00', notes: '' });
    setShowQuickBook(true);
  };

  const handleQuickBookSubmit = async (e) => {
    e.preventDefault();
    if (!quickBookData.service_id || !quickBookData.staff_id) { alert('Please select service and staff'); return; }
    try {
      const response = await axios.post('/api/bookings', {
        contact_id: quickBookCustomer.id, service_id: parseInt(quickBookData.service_id),
        staff_id: parseInt(quickBookData.staff_id), booking_date: quickBookData.booking_date,
        start_time: quickBookData.start_time, notes: quickBookData.notes
      });
      if (response.data.success) { alert('Booking created!'); setShowQuickBook(false); }
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
  };

  const openSMS = (customer) => {
    setSmsCustomer(customer);
    setSmsMessage('');
    setShowSMS(true);
  };

  const sendQuickSMS = async () => {
    if (!smsMessage.trim()) return;
    setSmsSending(true);
    try {
      await axios.post('/api/sms/send', { to: smsCustomer.phone, message: smsMessage, contact_id: smsCustomer.id });
      alert('Message sent!');
      setShowSMS(false);
    } catch (err) {
      var phone = cleanPhone(smsCustomer.phone);
      window.open('sms:' + phone + '?body=' + encodeURIComponent(smsMessage), '_self');
      setShowSMS(false);
    }
    setSmsSending(false);
  };

  const closeModal = () => {
    setShowModal(false); setEditingCustomer(null);
    setFormData({ first_name: '', last_name: '', phone: '', email: '', address: '', city: '', state: '', zip: '', birthday: '', notes: '' });
  };

  var inp = { width: '100%', background: '#12121f', border: '1px solid #1e1e3a', color: '#fff', padding: '11px 14px', borderRadius: 8, fontSize: '14px', outline: 'none' };
  var lbl = { display: 'block', color: '#888', fontSize: '13px', fontWeight: '600', marginBottom: '6px' };
  var contactBtn = function(bg) { return { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 14px', borderRadius: 8, background: bg, color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all .15s', textDecoration: 'none', flex: 1 }; };
  var btn = function(bg) { return { background: bg, color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 10, fontWeight: '600', fontSize: '14px', cursor: 'pointer', transition: 'all .15s' }; };
  var badgeStyle = function(bg, color) { return { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: '11px', fontWeight: '700', background: bg, color: color }; };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#06060f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#666', fontSize: '18px' }}>Loading customers...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#06060f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0d0d1f', padding: '32px', borderRadius: 16, border: '1px solid #1e1e3a', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ color: '#ef4444', fontWeight: '700', marginBottom: '8px' }}>Failed to load</div>
        <button onClick={loadCustomers} style={btn('#7c3aed')}>Retry</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#06060f', padding: '24px' }}>
      <style>{
        '@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }' +
        '@media (max-width: 768px) { .cust-grid { grid-template-columns: 1fr !important; } .cust-stats { display: grid !important; grid-template-columns: repeat(2, 1fr) !important; } .detail-panel { width: 100% !important; } .cust-header { flex-direction: column !important; } }'
      }</style>

      {/* Header */}
      <div className="cust-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: 0 }}>Customers</h1>
          <p style={{ color: '#555', fontSize: '14px', marginTop: '4px' }}>{customers.length} total · {stats.vip} VIP · {stats.newThisMonth} new this month</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => { import('./lib/exportCSV').then(function(m) { m.exportCSV('customers'); }); }} style={{ ...btn('#1e1e3a'), padding: '10px 16px', fontSize: '13px' }}>
            📥 Export
          </button>
          <button onClick={() => setShowModal(true)} style={{ ...btn('#7c3aed'), padding: '10px 20px' }}>
            + Add Customer
          </button>
        </div>
      </div>

      {/* Stat filters */}
      <div className="cust-stats" style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All', count: stats.total, icon: '👥' },
          { key: 'vip', label: 'VIP ($500+)', count: stats.vip, icon: '⭐' },
          { key: 'new', label: 'New (30d)', count: stats.newThisMonth, icon: '🆕' },
          { key: 'inactive', label: 'Inactive (90d+)', count: stats.inactive, icon: '💤' },
        ].map(function(f) {
          var active = filterBy === f.key;
          return (
            <div key={f.key} onClick={() => setFilterBy(f.key)} style={{
              padding: '10px 16px', borderRadius: 10, border: '1px solid ' + (active ? '#7c3aed44' : '#1e1e3a'),
              background: active ? '#7c3aed15' : '#0d0d1f', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
            }}>
              <span>{f.icon}</span>
              <span style={{ fontSize: '18px', fontWeight: '800', color: active ? '#a78bfa' : '#fff' }}>{f.count}</span>
              <span style={{ fontSize: '13px', color: '#666' }}>{f.label}</span>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text" placeholder="Search name, phone, email, notes..."
          value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }}
          style={{ flex: 1, minWidth: '200px', background: '#0d0d1f', border: '1px solid #1e1e3a', color: '#fff', padding: '12px 16px', borderRadius: 10, fontSize: '14px', outline: 'none' }}
        />
        <select value={sortBy} onChange={function(e) { setSortBy(e.target.value); }} style={{ background: '#0d0d1f', border: '1px solid #1e1e3a', color: '#999', padding: '12px 14px', borderRadius: 10, fontSize: '13px', outline: 'none', cursor: 'pointer' }}>
          <option value="name">Sort: Name</option>
          <option value="spent">Sort: Top Spenders</option>
          <option value="visits">Sort: Most Visits</option>
          <option value="recent">Sort: Recent Visit</option>
        </select>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid ' + (viewMode === 'grid' ? '#7c3aed44' : '#1e1e3a'), background: viewMode === 'grid' ? '#7c3aed15' : '#0d0d1f', color: viewMode === 'grid' ? '#a78bfa' : '#666', cursor: 'pointer', fontSize: '16px' }} onClick={() => setViewMode('grid')} title="Grid">▦</button>
          <button style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid ' + (viewMode === 'list' ? '#7c3aed44' : '#1e1e3a'), background: viewMode === 'list' ? '#7c3aed15' : '#0d0d1f', color: viewMode === 'list' ? '#a78bfa' : '#666', cursor: 'pointer', fontSize: '16px' }} onClick={() => setViewMode('list')} title="List">☰</button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="cust-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
          {processedCustomers.map(function(customer) {
            var badge = getCustomerBadge(customer);
            var phone = cleanPhone(customer.phone);
            return (
              <div key={customer.id}
                onClick={() => { setSelectedCustomer(customer); axios.get('/api/memberships/subscriptions').then(r => { const subs = r.data.subscriptions || []; const sub = subs.find(s => String(s.contact_id) === String(customer.id) && s.status === 'active'); setCustomerMembership(sub || null); }).catch(() => setCustomerMembership(null)); axios.get('/api/gift-cards').then(r => { const cards = (r.data.gift_cards || []).filter(g => g.purchased_by === customer.id || g.contact_id === customer.id); setCustomerGiftCards(cards); }).catch(() => setCustomerGiftCards([])); }}
                style={{ background: '#0d0d1f', borderRadius: 14, border: '1px solid #1e1e3a', padding: '20px', cursor: 'pointer', transition: 'all .15s' }}
                onMouseEnter={function(e) { e.currentTarget.style.borderColor = '#7c3aed44'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.borderColor = '#1e1e3a'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>{customer.first_name} {customer.last_name}</span>
                      {badge && <span style={badgeStyle(badge.bg, badge.color)}>{badge.label}</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#555' }}>Last visit: {formatLastVisit(customer.last_visit)}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
                  {customer.phone && <a href={'tel:' + phone} onClick={function(e) { e.stopPropagation(); }} style={{ color: '#a78bfa', fontSize: '13px', textDecoration: 'none' }}>📞 {customer.phone}</a>}
                  {customer.email && <a href={'mailto:' + customer.email} onClick={function(e) { e.stopPropagation(); }} style={{ color: '#60a5fa', fontSize: '13px', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉️ {customer.email}</a>}
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', padding: '10px 12px', background: '#12121f', borderRadius: 8 }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>{customer.total_visits || 0}</div>
                    <div style={{ fontSize: '11px', color: '#555' }}>Visits</div>
                  </div>
                  <div style={{ width: '1px', background: '#1e1e3a' }} />
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e' }}>${parseFloat(customer.total_spent || 0).toFixed(0)}</div>
                    <div style={{ fontSize: '11px', color: '#555' }}>Spent</div>
                  </div>
                  <div style={{ width: '1px', background: '#1e1e3a' }} />
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b' }}>{customer.loyalty_points || 0}</div>
                    <div style={{ fontSize: '11px', color: '#555' }}>Loyalty Pts</div>
                  </div>
                </div>

                {customer.notes && <div style={{ fontSize: '12px', color: '#555', marginBottom: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📝 {customer.notes}</div>}

                <div style={{ display: 'flex', gap: '6px' }}>
                  <a href={'tel:' + phone} onClick={function(e) { e.stopPropagation(); }} style={contactBtn('#1e3a5f')}>📞 Call</a>
                  <button onClick={function(e) { e.stopPropagation(); openSMS(customer); }} style={contactBtn('#1a3a2a')}>💬 Text</button>
                  <button onClick={function(e) { e.stopPropagation(); handleQuickBook(customer); }} style={contactBtn('#2d1b69')}>📅 Book</button>
                  <button onClick={function(e) { e.stopPropagation(); navigate('/app/checkout?customer=' + customer.id); }} style={contactBtn('#1a3a2a')}>💳 Pay</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div style={{ background: '#0d0d1f', borderRadius: 14, border: '1px solid #1e1e3a', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e1e3a' }}>
                {['Name', 'Phone', 'Email', 'Visits', 'Spent', 'Last Visit', 'Actions'].map(function(h) {
                  return <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {processedCustomers.map(function(customer) {
                var badge = getCustomerBadge(customer);
                var phone = cleanPhone(customer.phone);
                return (
                  <tr key={customer.id} style={{ borderBottom: '1px solid #1e1e3a', cursor: 'pointer' }}
                    onClick={() => setSelectedCustomer(customer)}
                    onMouseEnter={function(e) { e.currentTarget.style.background = '#12121f'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{customer.first_name} {customer.last_name}</span>
                        {badge && <span style={badgeStyle(badge.bg, badge.color)}>{badge.label}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>{customer.phone && <a href={'tel:' + phone} onClick={function(e) { e.stopPropagation(); }} style={{ color: '#a78bfa', textDecoration: 'none', fontSize: '13px' }}>{customer.phone}</a>}</td>
                    <td style={{ padding: '14px 16px' }}>{customer.email && <a href={'mailto:' + customer.email} onClick={function(e) { e.stopPropagation(); }} style={{ color: '#60a5fa', textDecoration: 'none', fontSize: '13px' }}>{customer.email}</a>}</td>
                    <td style={{ padding: '14px 16px', color: '#fff', fontWeight: '600', fontSize: '13px' }}>{customer.total_visits || 0}</td>
                    <td style={{ padding: '14px 16px', color: '#22c55e', fontWeight: '600', fontSize: '13px' }}>${parseFloat(customer.total_spent || 0).toFixed(0)}</td>
                    <td style={{ padding: '14px 16px', color: '#666', fontSize: '13px' }}>{formatLastVisit(customer.last_visit)}</td>
                    <td style={{ padding: '14px 8px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <a href={'tel:' + phone} onClick={function(e) { e.stopPropagation(); }} style={{ padding: '6px 8px', borderRadius: 6, background: '#1e3a5f', color: '#fff', textDecoration: 'none', fontSize: '12px' }} title="Call">📞</a>
                        <button onClick={function(e) { e.stopPropagation(); openSMS(customer); }} style={{ padding: '6px 8px', borderRadius: 6, background: '#1a3a2a', color: '#fff', border: 'none', fontSize: '12px', cursor: 'pointer' }} title="Text">💬</button>
                        <button onClick={function(e) { e.stopPropagation(); handleQuickBook(customer); }} style={{ padding: '6px 8px', borderRadius: 6, background: '#2d1b69', color: '#fff', border: 'none', fontSize: '12px', cursor: 'pointer' }} title="Book">📅</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty */}
      {processedCustomers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <p style={{ color: '#666', fontSize: '16px', marginBottom: '8px' }}>
            {searchTerm ? 'No customers match your search' : filterBy !== 'all' ? 'No ' + filterBy + ' customers' : 'No customers yet'}
          </p>
          {(searchTerm || filterBy !== 'all') && (
            <button onClick={() => { setSearchTerm(''); setFilterBy('all'); }} style={{ color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px' }}>Clear filters</button>
          )}
        </div>
      )}

      {/* Detail Panel */}
      {selectedCustomer && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 39 }} onClick={() => setSelectedCustomer(null)} />
          <div className="detail-panel" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '440px', maxWidth: '100vw', background: '#1a1c2e', borderLeft: '1px solid #1e1e3a', zIndex: 40, overflowY: 'auto', padding: '28px', animation: 'slideIn .2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#fff', margin: 0 }}>{selectedCustomer.first_name} {selectedCustomer.last_name}</h2>
              <button onClick={() => setSelectedCustomer(null)} style={{ color: '#666', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>

            {customerMembership && (
              <div style={{ marginBottom: '8px' }}>
                <span style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>👑 {customerMembership.membership_plans?.name || 'Member'} · Active</span>
              </div>
            )}
            {getCustomerBadge(selectedCustomer) && (
              <div style={{ marginBottom: '16px' }}>
                <span style={badgeStyle(getCustomerBadge(selectedCustomer).bg, getCustomerBadge(selectedCustomer).color)}>{getCustomerBadge(selectedCustomer).label}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {selectedCustomer.phone && (
                <>
                  <a href={'tel:' + cleanPhone(selectedCustomer.phone)} style={contactBtn('#1e3a5f')}>📞 Call</a>
                  <a href={'sms:' + cleanPhone(selectedCustomer.phone)} style={contactBtn('#1a3a2a')}>💬 Text</a>
                </>
              )}
              {selectedCustomer.email && <a href={'mailto:' + selectedCustomer.email} style={contactBtn('#3a2a1a')}>✉️ Email</a>}
              <button onClick={() => openSMS(selectedCustomer)} style={contactBtn('#1a3a2a')}>📲 Quick SMS</button>
            </div>

            <div style={{ background: '#12121f', borderRadius: 10, padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', marginBottom: '12px' }}>Contact Info</div>
              {selectedCustomer.phone && <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '8px' }}>📞 {selectedCustomer.phone}</div>}
              {selectedCustomer.email && <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '8px' }}>✉️ {selectedCustomer.email}</div>}
              {selectedCustomer.city && <div style={{ fontSize: '14px', color: '#ccc', marginBottom: '8px' }}>📍 {selectedCustomer.address ? selectedCustomer.address + ', ' : ''}{selectedCustomer.city}, {selectedCustomer.state} {selectedCustomer.zip}</div>}
              {selectedCustomer.birthday && <div style={{ fontSize: '14px', color: '#ccc' }}>🎂 {new Date(selectedCustomer.birthday).toLocaleDateString()}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <div style={{ background: '#12121f', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#fff' }}>{selectedCustomer.total_visits || 0}</div>
                <div style={{ fontSize: '11px', color: '#555' }}>Visits</div>
              </div>
              <div style={{ background: '#12121f', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#22c55e' }}>${parseFloat(selectedCustomer.total_spent || 0).toFixed(0)}</div>
                <div style={{ fontSize: '11px', color: '#555' }}>Spent</div>
              </div>
              <div style={{ background: '#12121f', borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#f59e0b' }}>{selectedCustomer.loyalty_points || 0}</div>
                <div style={{ fontSize: '11px', color: '#555' }}>Loyalty Pts</div>
              </div>
            </div>

            <div style={{ background: '#12121f', borderRadius: 10, padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', marginBottom: '8px' }}>Activity</div>
              <div style={{ fontSize: '13px', color: '#888' }}>Last visit: <span style={{ color: '#ccc' }}>{formatLastVisit(selectedCustomer.last_visit)}</span></div>
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>Customer since: <span style={{ color: '#ccc' }}>{selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : 'Unknown'}</span></div>
            </div>

                        {customerGiftCards.length > 0 && (
              <div style={{ background: '#12121f', borderRadius: 10, padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', marginBottom: '10px' }}>🎁 Gift Cards</div>
                {customerGiftCards.map(gc => (
                  <div key={gc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1e1e3a' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: '#ccc', fontFamily: 'monospace' }}>{gc.code}</div>
                      <div style={{ fontSize: '11px', color: '#555', marginTop: 2 }}>{gc.status}</div>
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: gc.balance > 0 ? '#22c55e' : '#555' }}>${parseFloat(gc.balance || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
            {selectedCustomer.notes && (
              <div style={{ background: '#12121f', borderRadius: 10, padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', marginBottom: '8px' }}>Notes</div>
                <div style={{ fontSize: '14px', color: '#ccc', lineHeight: 1.5 }}>{selectedCustomer.notes}</div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '20px' }}>
              <button onClick={() => { handleQuickBook(selectedCustomer); setSelectedCustomer(null); }} style={btn('#7c3aed')}>📅 Book Appointment</button>
              <button onClick={() => navigate('/app/checkout?customer=' + selectedCustomer.id)} style={btn('#166534')}>💳 Quick Checkout</button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => { handleEdit(selectedCustomer); setSelectedCustomer(null); }} style={{ ...btn('#1e1e3a'), flex: 1 }}>✏️ Edit</button>
                <button onClick={() => handleDelete(selectedCustomer.id)} style={{ ...btn('#3a1a1a'), flex: 1 }}>🗑️ Delete</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Quick SMS Modal */}
      {showSMS && smsCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }} onClick={() => setShowSMS(false)}>
          <div style={{ background: '#0d0d1f', borderRadius: 16, border: '1px solid #1e1e3a', maxWidth: '520px', width: '100%', padding: '28px' }} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>💬 Text {smsCustomer.first_name}</h2>
              <button onClick={() => setShowSMS(false)} style={{ color: '#666', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#12121f', borderRadius: 8, fontSize: '13px', color: '#888' }}>
              To: <span style={{ color: '#a78bfa' }}>{smsCustomer.phone}</span>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <textarea value={smsMessage} onChange={function(e) { setSmsMessage(e.target.value); }}
                placeholder={'Hi ' + smsCustomer.first_name + ', ...'}
                style={{ ...inp, height: '100px', resize: 'none', fontFamily: 'inherit' }} autoFocus />
              <div style={{ textAlign: 'right', fontSize: '12px', color: smsMessage.length > 160 ? '#ef4444' : '#555', marginTop: '4px' }}>{smsMessage.length}/160</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { label: '📅 Reminder', msg: 'Hi ' + smsCustomer.first_name + ', just a reminder about your upcoming appointment. See you soon!' },
                { label: '✅ Ready', msg: 'Hi ' + smsCustomer.first_name + ', your service is complete and ready for pickup! Let us know if you have any questions.' },
                { label: '🔁 Follow-up', msg: 'Hi ' + smsCustomer.first_name + ', it\'s been a while! We\'d love to see you again. Reply to book your next visit.' },
                { label: '🎉 Promo', msg: 'Hi ' + smsCustomer.first_name + ', we\'re running a special this week! Reply for details or call us to book.' },
              ].map(function(t) {
                return <button key={t.label} onClick={() => setSmsMessage(t.msg)} style={{ padding: '6px 12px', borderRadius: 6, background: '#1e1e3a', border: '1px solid #2a2a4a', color: '#999', fontSize: '12px', cursor: 'pointer' }}>{t.label}</button>;
              })}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={sendQuickSMS} disabled={!smsMessage.trim() || smsSending}
                style={{ ...btn('#166534'), flex: 1, opacity: smsMessage.trim() ? 1 : 0.4 }}>
                {smsSending ? 'Sending...' : '📤 Send via IVision'}
              </button>
              {smsCustomer.phone && (
                <a href={'sms:' + cleanPhone(smsCustomer.phone) + '?body=' + encodeURIComponent(smsMessage)}
                  style={{ ...contactBtn('#1e3a5f'), textDecoration: 'none', textAlign: 'center', flex: 1 }}>
                  📱 Open in Messages
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }} onClick={closeModal}>
          <div style={{ background: '#0d0d1f', borderRadius: 16, border: '1px solid #1e1e3a', maxWidth: '600px', width: '100%', padding: '28px', maxHeight: '90vh', overflowY: 'auto' }} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>{editingCustomer ? 'Edit Customer' : 'New Customer'}</h2>
              <button onClick={closeModal} style={{ color: '#666', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div><label style={lbl}>First Name *</label><input style={inp} value={formData.first_name} onChange={function(e) { setFormData({...formData, first_name: e.target.value}); }} required /></div>
                <div><label style={lbl}>Last Name</label><input style={inp} value={formData.last_name} onChange={function(e) { setFormData({...formData, last_name: e.target.value}); }} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div><label style={lbl}>Phone *</label><input style={inp} type="tel" value={formData.phone} onChange={function(e) { setFormData({...formData, phone: e.target.value}); }} placeholder="702-555-1234" required /></div>
                <div><label style={lbl}>Email</label><input style={inp} type="email" value={formData.email} onChange={function(e) { setFormData({...formData, email: e.target.value}); }} placeholder="name@email.com" /></div>
              </div>
              <div style={{ marginBottom: '12px' }}><label style={lbl}>Address</label><input style={inp} value={formData.address} onChange={function(e) { setFormData({...formData, address: e.target.value}); }} placeholder="123 Main St" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div><label style={lbl}>City</label><input style={inp} value={formData.city} onChange={function(e) { setFormData({...formData, city: e.target.value}); }} placeholder="Las Vegas" /></div>
                <div><label style={lbl}>State</label><input style={inp} value={formData.state} onChange={function(e) { setFormData({...formData, state: e.target.value}); }} placeholder="NV" maxLength="2" /></div>
                <div><label style={lbl}>ZIP</label><input style={inp} value={formData.zip} onChange={function(e) { setFormData({...formData, zip: e.target.value}); }} placeholder="89101" /></div>
              </div>
              <div style={{ marginBottom: '12px' }}><label style={lbl}>Birthday</label><input style={inp} type="date" value={formData.birthday} onChange={function(e) { setFormData({...formData, birthday: e.target.value}); }} /></div>
              <div style={{ marginBottom: '20px' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, height: '80px', resize: 'none', fontFamily: 'inherit' }} value={formData.notes} onChange={function(e) { setFormData({...formData, notes: e.target.value}); }} placeholder="Customer preferences, vehicle info..." /></div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ ...btn('#7c3aed'), flex: 1 }}>{editingCustomer ? '✅ Update' : '✅ Create'}</button>
                <button type="button" onClick={closeModal} style={{ ...btn('#1e1e3a'), flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Book Modal */}
      {showQuickBook && quickBookCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50 }} onClick={() => setShowQuickBook(false)}>
          <div style={{ background: '#0d0d1f', borderRadius: 16, border: '1px solid #1e1e3a', maxWidth: '520px', width: '100%', padding: '28px' }} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>📅 Book {quickBookCustomer.first_name}</h2>
              <button onClick={() => setShowQuickBook(false)} style={{ color: '#666', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleQuickBookSubmit}>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Service *</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={quickBookData.service_id} onChange={function(e) { setQuickBookData({...quickBookData, service_id: e.target.value}); }} required>
                  <option value="">Select service...</option>
                  {services.map(function(sv) { return <option key={sv.id} value={sv.id}>{sv.name} - ${sv.price} ({sv.duration}min)</option>; })}
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={lbl}>Staff *</label>
                <select style={{ ...inp, cursor: 'pointer' }} value={quickBookData.staff_id} onChange={function(e) { setQuickBookData({...quickBookData, staff_id: e.target.value}); }} required>
                  <option value="">Select staff...</option>
                  {staff.map(function(st) { return <option key={st.id} value={st.id}>{st.first_name} {st.last_name}</option>; })}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div><label style={lbl}>Date *</label><input style={inp} type="date" value={quickBookData.booking_date} onChange={function(e) { setQuickBookData({...quickBookData, booking_date: e.target.value}); }} required /></div>
                <div><label style={lbl}>Time *</label><input style={inp} type="time" value={quickBookData.start_time} onChange={function(e) { setQuickBookData({...quickBookData, start_time: e.target.value}); }} required /></div>
              </div>
              <div style={{ marginBottom: '20px' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, height: '60px', resize: 'none', fontFamily: 'inherit' }} value={quickBookData.notes} onChange={function(e) { setQuickBookData({...quickBookData, notes: e.target.value}); }} placeholder="Special requests..." /></div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ ...btn('#7c3aed'), flex: 1 }}>✅ Create Booking</button>
                <button type="button" onClick={() => setShowQuickBook(false)} style={{ ...btn('#1e1e3a'), flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
